import React, { useState, useMemo } from 'react'
import { ExternalLink, Clock } from 'lucide-react'
import { format } from 'date-fns'

function TableView({ 
  data, 
  selectedOrganisations, 
  yearFilter, 
  typeFilter, 
  verifiedFilter, 
  relevanceFilter = 0, 
  locationFilter, 
  costFilter, 
  registrationDeadlineFilter, 
  submissionDeadlineFilter, 
  memberSizeFilter,
  onReportIssue,
  onConferenceClick 
}) {
  const [sortField, setSortField] = useState('dates')
  const [sortDirection, setSortDirection] = useState('asc')

  const filteredConferences = useMemo(() => {
    if (!data?.conferences) return []

    return data.conferences.filter(conf => {
      // Organisation filter
      if (!selectedOrganisations.has(conf.organisationId)) return false

      // Year filter (multi-select)
      if (yearFilter.size > 0) {
        const confYear = conf.year ? conf.year.toString() : null
        const hasValidYear = confYear && yearFilter.has(confYear)
        const hasNotSpecified = !confYear && yearFilter.has('not_specified')
        if (!hasValidYear && !hasNotSpecified) return false
      }

      // Type filter (check organisation type) (multi-select)
      if (typeFilter.size > 0) {
        const org = data.organisations?.find(o => o.id === conf.organisationId)
        const orgType = org?.type || null
        const hasValidType = orgType && typeFilter.has(orgType)
        const hasNotSpecified = !orgType && typeFilter.has('not_specified')
        if (!hasValidType && !hasNotSpecified) return false
      }

      // Member size filter (check organisation size) (multi-select)
      if (memberSizeFilter.size > 0) {
        const org = data.organisations?.find(o => o.id === conf.organisationId)
        const memberCount = org?.memberCount
        let sizeCategory = null
        
        if (memberCount) {
          if (memberCount >= 1 && memberCount <= 99) sizeCategory = 'small'
          else if (memberCount >= 100 && memberCount <= 999) sizeCategory = 'medium'
          else if (memberCount >= 1000) sizeCategory = 'large'
        }
        
        const hasValidSize = sizeCategory && memberSizeFilter.has(sizeCategory)
        const hasNotSpecified = !memberCount && memberSizeFilter.has('not_specified')
        if (!hasValidSize && !hasNotSpecified) return false
      }

      // Location filter (multi-select)
      if (locationFilter.size > 0) {
        const location = conf.location?.toLowerCase() || ''
        let locationCategory = null
        
        if (location) {
          if (location.includes('auckland')) locationCategory = 'auckland'
          else if (location.includes('wellington')) locationCategory = 'wellington'
          else if (location.includes('christchurch')) locationCategory = 'christchurch'
          else if (location.includes('new zealand') || location.includes('nz') || 
                   location.includes('hamilton') || location.includes('dunedin') || 
                   location.includes('tauranga') || location.includes('rotorua')) locationCategory = 'other_nz'
          else if (location.includes('australia') || location.includes('sydney') || 
                   location.includes('melbourne') || location.includes('brisbane')) locationCategory = 'australia'
          else locationCategory = 'international'
        }
        
        const hasValidLocation = locationCategory && locationFilter.has(locationCategory)
        const hasNotSpecified = !location && locationFilter.has('not_specified')
        if (!hasValidLocation && !hasNotSpecified) return false
      }

      // Cost filter (multi-select)
      if (costFilter.size > 0) {
        const cost = conf.registrationCost
        let costCategory = null
        
        if (cost) {
          if (typeof cost === 'string') {
            const costLower = cost.toLowerCase()
            if (costLower.includes('free') || costLower.includes('$0') || costLower === '0') {
              costCategory = 'free'
            } else {
              // Parse cost ranges - simplified logic
              const match = costLower.match(/\$?(\d+)/)
              if (match) {
                const amount = parseInt(match[1])
                if (amount >= 1 && amount <= 499) costCategory = 'low'
                else if (amount >= 500 && amount <= 1499) costCategory = 'medium'
                else if (amount >= 1500) costCategory = 'high'
              }
            }
          }
        }
        
        const hasValidCost = costCategory && costFilter.has(costCategory)
        const hasNotSpecified = !cost && costFilter.has('not_specified')
        if (!hasValidCost && !hasNotSpecified) return false
      }

      // Registration deadline filter (multi-select)
      if (registrationDeadlineFilter.size > 0) {
        const deadline = conf.registrationDeadline
        let deadlineCategory = null
        
        if (deadline) {
          const deadlineDate = new Date(deadline)
          const now = new Date()
          const daysUntil = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24))
          
          if (daysUntil < 0) deadlineCategory = 'overdue'
          else if (daysUntil >= 1 && daysUntil <= 7) deadlineCategory = 'soon'
          else if (daysUntil >= 8 && daysUntil <= 30) deadlineCategory = 'upcoming'
          else if (daysUntil > 30) deadlineCategory = 'future'
        }
        
        const hasValidDeadline = deadlineCategory && registrationDeadlineFilter.has(deadlineCategory)
        const hasNotSpecified = !deadline && registrationDeadlineFilter.has('not_specified')
        if (!hasValidDeadline && !hasNotSpecified) return false
      }

      // Submission deadline filter (multi-select)
      if (submissionDeadlineFilter.size > 0) {
        const deadline = conf.paperSubmissionDeadline
        let deadlineCategory = null
        
        if (deadline) {
          const deadlineDate = new Date(deadline)
          const now = new Date()
          const daysUntil = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24))
          
          if (daysUntil < 0) deadlineCategory = 'closed'
          else if (daysUntil >= 1 && daysUntil <= 7) deadlineCategory = 'closing_soon'
          else if (daysUntil >= 8 && daysUntil <= 30) deadlineCategory = 'open_soon'
          else if (daysUntil > 30) deadlineCategory = 'open_future'
        }
        
        const hasValidDeadline = deadlineCategory && submissionDeadlineFilter.has(deadlineCategory)
        const hasNotSpecified = !deadline && submissionDeadlineFilter.has('not_specified')
        if (!hasValidDeadline && !hasNotSpecified) return false
      }

      // Verification filter (multi-select)
      if (verifiedFilter.size > 0) {
        const isVerified = conf.verified === true
        const hasVerified = isVerified && verifiedFilter.has('verified')
        const hasUnverified = !isVerified && verifiedFilter.has('unverified')
        if (!hasVerified && !hasUnverified) return false
      }

      // Relevance filter (still a range slider)
      const relevance = conf.relevancePercentage || 0
      if (relevance < relevanceFilter) return false

      return true
    })
  }, [data, selectedOrganisations, yearFilter, typeFilter, verifiedFilter, relevanceFilter, locationFilter, costFilter, registrationDeadlineFilter, submissionDeadlineFilter, memberSizeFilter])

  const sortedConferences = useMemo(() => {
    const sorted = [...filteredConferences].sort((a, b) => {
      let aValue = a[sortField]
      let bValue = b[sortField]

      // Handle special cases
      if (sortField === 'dates') {
        aValue = a.dates?.[0] || '9999-12-31'
        bValue = b.dates?.[0] || '9999-12-31'
      }

      if (sortField === 'confidence') {
        aValue = a.confidence || 0
        bValue = b.confidence || 0
      }

      if (sortField === 'relevancePercentage') {
        aValue = a.relevancePercentage || 0
        bValue = b.relevancePercentage || 0
      }

      if (aValue == null) aValue = ''
      if (bValue == null) bValue = ''

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    })

    return sorted
  }, [filteredConferences, sortField, sortDirection])

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD'
    try {
      return format(new Date(dateString), 'MMM d')
    } catch {
      return dateString
    }
  }

  const formatDateRange = (dates) => {
    if (!dates || dates.length === 0) return 'TBD'
    
    if (dates.length === 1) {
      return formatDate(dates[0])
    }
    
    const start = formatDate(dates[0])
    const end = formatDate(dates[dates.length - 1])
    
    if (start === end) return start
    return `${start} - ${end}`
  }

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.7) return 'confidence-high'
    if (confidence >= 0.4) return 'confidence-medium'
    return 'confidence-low'
  }

  const getConfidenceText = (confidence) => {
    if (confidence >= 0.7) return 'High'
    if (confidence >= 0.4) return 'Medium'
    return 'Low'
  }

  const handleRowClick = (conference) => {
    onConferenceClick(conference)
  }


  if (!data?.conferences || data.conferences.length === 0) {
    return (
      <div className="empty-state">
        <h3>No conference data available</h3>
        <p>Run the scraper to populate conference information.</p>
      </div>
    )
  }

  if (filteredConferences.length === 0) {
    return (
      <div className="empty-state">
        <h3>No conferences match your filters</h3>
        <p>Try adjusting your filter settings to see more results.</p>
      </div>
    )
  }

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('name')}>
              Conference Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('organisationName')}>
              Organisation {sortField === 'organisationName' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('year')}>
              Year {sortField === 'year' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('dates')}>
              Dates {sortField === 'dates' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('location')}>
              Location {sortField === 'location' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('registrationCost')}>
              Cost {sortField === 'registrationCost' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('confidence')}>
              Confidence {sortField === 'confidence' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('relevancePercentage')}>
              Relevance {sortField === 'relevancePercentage' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('paperSubmissionDeadline')}>
              Submission Deadline {sortField === 'paperSubmissionDeadline' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedConferences.map(conference => (
            <tr key={conference.id} onClick={() => handleRowClick(conference)} className="table-row">
              <td>
                <div className="table-cell-content">
                  <div className="table-cell-main">
                    <div className="conference-name">{conference.name || 'Unnamed Conference'}</div>
                    {conference.theme && (
                      <div className="conference-theme">
                        {conference.theme}
                      </div>
                    )}
                  </div>
                  {conference.conferenceUrl && (
                    <a
                      href={conference.conferenceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="external-link"
                      title="Visit conference website"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              </td>
              <td>
                <div className="table-cell-content">
                  <div className="table-cell-main">
                    <div className = "organisation-name">
                      {conference.organisationName}
                    </div>
                    {conference.actualOrganiser && conference.actualOrganiser !== conference.organisationName && (
                      <div className="actual-organiser-note">
                        Actually organised by: {conference.actualOrganiser}
                      </div>
                    )}
                    {(() => {
                      const org = data.organisations?.find(o => o.id === conference.organisationId)
                      return org?.type && (
                        <div className="organisation-secondary">
                          {org.type.replace('_', ' ')}
                        </div>
                      )
                    })()}
                  </div>
                  {(() => {
                    const org = data.organisations?.find(o => o.id === conference.organisationId)
                    return org?.website && (
                      <a
                        href={org.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="external-link"
                        title="Visit organisation website"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={16} />
                      </a>
                    )
                  })()}
                </div>
              </td>
              <td>{conference.year}</td>
              <td>
                <div className="table-icon-text">
                  {formatDateRange(conference.dates)}
                </div>
              </td>
              <td>
                <div className="table-icon-text">
                  <div>
                    {conference.location || 'TBD'}
                    {conference.venue && (
                      <div className="venue-text">
                        {conference.venue}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <div className="table-icon-text">
                  <div className="conference-cost">
                    {(() => {
                      const cost = conference.registrationCost;
                      if (!cost) return 'TBD';
                      
                      // Handle object with pricing tiers
                      if (typeof cost === 'object') {
                        const entries = Object.entries(cost);
                        if (entries.length === 0) return 'TBD';
                        
                        // Show first pricing tier, with indicator if multiple
                        const [, firstValue] = entries[0];
                        const hasMultiple = entries.length > 1;
                        
                        return (
                          <div>
                            <div>{firstValue}</div>
                            {hasMultiple && (
                              <div className="cost-pricing-tier">
                                +{entries.length - 1} more tiers
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      // Handle string/number
                      return cost;
                    })()}
                    {conference.registrationDeadline && (
                      <div className="registration-deadline">
                        <Clock size={12} />
                        Due: {formatDate(conference.registrationDeadline)}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <span className={`confidence-badge ${getConfidenceBadge(conference.confidence || 0)}`}>
                  {getConfidenceText(conference.confidence || 0)}
                </span>
                {!conference.verified && (
                  <div className="unverified-text">
                    Unverified
                  </div>
                )}
              </td>
              <td>
                <div>
                  {conference.relevancePercentage ? (
                    <span className={`relevance-badge ${
                      conference.relevancePercentage >= 80 ? 'relevance-high' : 
                      conference.relevancePercentage >= 60 ? 'relevance-medium' : 'relevance-low'
                    }`}>
                      {conference.relevancePercentage}%
                    </span>
                  ) : (
                    <span className="relevance-none">-</span>
                  )}
                  {conference.relevanceReason && (
                    <div className="relevance-reason">
                      {conference.relevanceReason}
                    </div>
                  )}
                </div>
              </td>
              <td>
                <div className="table-icon-text">
                  <Clock size={14} color="#6b7280" />
                  <div>
                    {conference.paperSubmissionDeadline ? (
                      <div>
                        {formatDate(conference.paperSubmissionDeadline)}
                        {(() => {
                          if (!conference.paperSubmissionDeadline) return null;
                          const deadline = new Date(conference.paperSubmissionDeadline);
                          const now = new Date();
                          const daysUntil = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
                          
                          if (daysUntil < 0) {
                            return (
                              <div className="submission-status-closed">Closed</div>
                            );
                          } else if (daysUntil <= 7) {
                            return (
                              <div className="submission-status-closing">Closing soon</div>
                            );
                          } else if (daysUntil <= 30) {
                            return (
                              <div className="submission-status-open">Open</div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    ) : (
                      <span className="submission-status-tbd">TBD</span>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      <div className="table-footer">
        Showing {filteredConferences.length} of {data.conferences.length} conferences
      </div>
      
    </div>
  )
}

export default TableView